import React, { useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Header,
  SpaceBetween,
  ContentLayout,
} from "@cloudscape-design/components";
import { ConfigurableForm } from "../common/Forms";
import { rvmFormSchema } from "./schemas/rvm-form-schema";
import { useFormStore } from "../../store/form-stores";
import { useSessionFormId } from "../../hooks/useSessionFormId";
import { ROUTES } from "../../constants";
import { logger } from '../../utils/logger';

export const RVMCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { setFormData, getFormData } = useFormStore();

  // Generate session-specific form ID for this tab (RVM is always create mode)
  const [sessionFormId, cleanupSession] = useSessionFormId(rvmFormSchema.id, false, undefined);
  
  // Cleanup form data on component unmount
  useEffect(() => {
    return () => {
      setFormData(sessionFormId, {});
      cleanupSession();
    };
  }, [setFormData, sessionFormId, cleanupSession]);

  // Handle cancel action
  const handleCancel = useCallback(() => {
    if (
      confirm(
        "Are you sure you want to cancel? Any unsaved changes will be lost."
      )
    ) {
      // Clear all form data from store
      setFormData(sessionFormId, {});
      cleanupSession();
      navigate(ROUTES.DASHBOARD);
    }
  }, [setFormData, sessionFormId, cleanupSession, navigate]);

  // Handle save action (placeholder for future implementation)
  const handleSave = useCallback(async (data: any) => {
    logger.info("RVM form submitted:", {
      formData: data,
      timestamp: new Date().toISOString(),
    });

    // Placeholder for future API integration
    alert(`RVM Analysis "${data.analysisName || "Untitled"}" data logged to console!`);
  }, []);

  // Get initial form data with default values - memoized to prevent re-renders
  const initialValues = useMemo(() => {
    const savedData = getFormData(sessionFormId);

    // Apply default values from schema
    const defaultValues: any = {};
    rvmFormSchema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          defaultValues[field.name] = field.defaultValue;
        }
      });
    });

    return { ...defaultValues, ...savedData };
  }, []); // Empty dependency array since we only want this calculated once on mount

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          description="Rate-Volume-Mix variance analysis configuration"
        >
          Create RVM Analysis
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* The Configurable Form */}
        <ConfigurableForm
          workflowId={sessionFormId}
          schema={rvmFormSchema}
          initialValues={initialValues}
          showHeader={false}
          actionButtons={[
            { text: 'Cancel', variant: 'secondary', position: 'left', handler: handleCancel, bypassValidation: true },
            { text: 'Save', variant: 'primary', position: 'right', handler: handleSave }
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
};
